import React, { useRef } from 'react';
import { QuestRoute } from '../../../api/QuestRoutes';
import { formatCurrency, generateId, parseDateFromTimestamp, shortHandDate, statusColors } from '../../../utility/functions';
import Dot from '../../../Dot';
import Table from '../../../table/Table';
import LinkTo from '../../../forms/LinkTo';
import Button from '../../../forms/Button';
import Form from '../../../forms/Form';
import HamQuest from '../../../api/HamQuestGiver';
import Select from '../../../forms/Select';
import { HSOptions } from '../../../../constants/HSOptions';
import Row from '../../../layout/Row';
import { IsAuthorized, userRoles } from '../../../RoleProtected';
import ModalFactory from '../../../Modal';
import ManagementRow from '../../../table/ManagementRow';

const Invoices = ({ endpoint = QuestRoute.reports.invoices, justReport, ...props }) => {

    const isAuthorized = IsAuthorized(userRoles.viewCustomers);

    const billingInfoRef = useRef({});

    const updateStatus = (rowData) => {
        const id = generateId();

        ModalFactory.show({
            id,
            title: 'Update Invoice Status',
            body: (
                <Form
                    inputs={{
                        status: rowData.invoiceStatus,
                        paymentType: '',
                    }}
                    onSubmit={async (values) => {

                        // If not setting Pending status, submit normally
                        if (values.status !== 'Pending') {
                            return HamQuest.put(
                                QuestRoute.invoice.status(rowData.invoiceId),
                                values
                            ).then(() => {
                                window.DateRange();
                                ModalFactory.close(id);
                            });
                        }

                        // If setting Pending status, check if invoice is for current billing period
                        await handlePendingSubmit({
                            id,
                            rowData,
                            values,
                            billingInfoRef
                        });
                    }}
                    text='Update'
                    submitting='Updating'
                >
                    {({ values }) => (
                        <>
                            <Select
                                label='Status'
                                name='status'
                                options={HSOptions.invoice.status}
                                nullable
                                required
                            />

                            {values.status === 'Paid' && (
                                <Select
                                    label='Payment Type'
                                    name='paymentType'
                                    options={HSOptions.invoice.paymentType}
                                    required
                                />
                            )}
                        </>
                    )}
                </Form>
            )
        });
    };

    const handlePendingSubmit = async ({ id, rowData, values, billingInfoRef }) => {
        // check if invoice from a previous billing period to warn about possible duplicate changes
        const invoiceId = rowData.invoiceId;

        //cache billing info results to save API calls
        let cached = billingInfoRef.current[invoiceId];
        if (!cached) {
            await getBillingDetailsAndExecute(rowData, (billingInfo) => {
                const invoiceDate = parseDateFromTimestamp(rowData.invoiceDate);
                const prevBillingDate = computePrevBillingDate(
                    billingInfo.nextBillingDate,
                    billingInfo.billingCycle
                );

                const isPreviousPeriod = prevBillingDate > invoiceDate;

                billingInfoRef.current[invoiceId] = { isPreviousPeriod };
                cached = billingInfoRef.current[invoiceId];
            });
        }

        // If not previous period, go ahead and submit form
        if (!cached.isPreviousPeriod) {
            return HamQuest.put(
                QuestRoute.invoice.status(invoiceId),
                values
            ).then(() => {
                window.DateRange();
                ModalFactory.close(id);
            });
        }

        // Otherwise show warning modal
        showBillingWarning(id, invoiceId, values.status);
    };

    const computePrevBillingDate = (nextBillingDate, billingCycle) => {
        const cycle = billingCycle ?? 1;

        const date = parseDateFromTimestamp(nextBillingDate);

        const year = date.getFullYear();
        const month = date.getMonth() - cycle;
        const day = date.getDate();

        return new Date(year, month, day);
    };

    const getBillingDetailsAndExecute = async (rowData, callback) => {
        const modalId = generateId();

        if (!rowData.customerId && !rowData.loyaltyAccountId) {
            ModalFactory.show({
                title: 'Error',
                type: 'error',
                body: <>Error loading billing details for customer.</>
            });
            return;
        }

        const fetcher = rowData.isAppUser
            ? () => HamQuest.get(QuestRoute.user.washClub(rowData.customerId))
            : () => HamQuest.get(QuestRoute.loyaltyUser.info(rowData.loyaltyAccountId));

        await ModalFactory.promise({
            id: modalId,
            title: 'Loading billing details',
            callback: async () => {
                const res = await fetcher();

                const billingInfo = {
                    billingDay: res.billingDay,
                    billingCycle: res.billingCycle,
                    nextBillingDate: res.nextBillingDate
                };

                callback(billingInfo);
            }
        })
        .then(() => ModalFactory.close(modalId));
    };

    const showBillingWarning = (id, invoiceId, newStatus) => {
        if (newStatus !== 'Pending') return false;

        const buttons = [
            <Button label='Cancel Invoice' onClick={() => {
                HamQuest.put(QuestRoute.invoice.status(invoiceId), { status: 'Canceled' })
                    .then(() => {
                        window.DateRange();
                        ModalFactory.close(id);
                    });
            }}/>,
            <Button label='Set As Pending' onClick={() => {
                HamQuest.put(QuestRoute.invoice.status(invoiceId), { status: 'Pending' })
                    .then(() => {
                        window.DateRange();
                        ModalFactory.close(id);
                    });
            }}/>
        ];

        ModalFactory.update({
            id,
            title: `Set Invoice Status to Pending?`,
            type: 'warning',
            body: (
                <div style={{ maxWidth: '600px' }}>
                    <p>
                        It appears Invoice # {invoiceId} is from a previous billing period.
                        Performing this action may result in duplicate charges.
                    </p>
                    <p>
                        If you wish to reactivate the wash club, it's recommended to cancel this invoice
                        and use the <b>Re-Sign Up</b> button on the customer page.
                    </p>
                </div>
            ),
            buttons
        });
    };

    const tableConfig = {
        endpoint: endpoint,
        isReport: true,
        searchLimit: justReport ? 366 : 91,
        startDate: justReport ? new Date(new Date().setFullYear(new Date().getFullYear() - 1)) : false,
        colSpacing: [10, 5, 7, 10, 18, 7, 8, 8, 7, 20],
        headers: [
            {
                label: 'Name',
                propKey: 'accountName',
            }, {
                label: 'ID',
                propKey: 'invoiceId',
            }, {
                label: 'Date',
                propKey: 'invoiceDate',
                type: 'date'
            }, {
                label: 'Club Status',
                propKey: 'clubStatus',
                options: true
            }, {
                label: 'Status',
                propKey: 'invoiceStatus',
                options: true
            }, {
                label: 'Total',
                propKey: 'total',
            }, {
                label: 'Billing',
                propKey: 'isAutoBilled',
                options: true
            }, {
                label: 'Paid',
                propKey: 'datePaid',
                type: 'date'
            }, {
                label: 'Record',
                propKey: 'transRecordId',
            },
            {
                label: 'Description',
                propKey: 'description',
            }
        ],
        quickSort: justReport ? false : [
            {
                label: 'Paid',
                propKey: 'invoiceStatus',
                query: 'Paid',
                color: '#21D59B'
            }, {
                label: 'Pending',
                propKey: 'invoiceStatus',
                query: 'Pending',
                color: '#ffb100'
            }, {
                label: 'Charge Error',
                propKey: 'invoiceStatus',
                query: 'ChargeError',
                color: '#bf0d14'
            }, {
                label: 'Canceled',
                propKey: 'invoiceStatus',
                query: 'Canceled',
                color: '#44474e'
            }, {
                label: 'Automatic Billing',
                propKey: 'isAutoBilled',
                query: 'true',
                color: '#b5a4e8'
            }, {
                label: 'Manual Billing',
                propKey: 'isAutoBilled',
                query: 'false',
                color: '#a77b61'
            }
        ],
        renderFormat: (e) => (
            <ManagementRow
                key={generateId()}
                controls={[]}
            >
                <td>
                    {isAuthorized ?
                        <LinkTo toAccount={e}>{e.accountName}</LinkTo> : <div>{e.accountName}</div>}
                </td>
                <td>
                    <LinkTo target='_blank' to={'/reporting/Sales/Invoice/' + e.invoiceId}>{e.invoiceId}</LinkTo>
                </td>
                <td>{shortHandDate(e.invoiceDate)}</td>
                <td>
                    <Row>
                        <Dot color={statusColors[e.clubStatus]} />
                        <span>
                            {e.clubStatus}
                        </span>
                    </Row>
                </td>
                <td>
                    {(e.invoiceStatus === 'Pending' || e.invoiceStatus === 'ChargeError') ?
                        <>
                            <Button onClick={() => updateStatus(e)}>
                                <Dot color={statusColors[e.invoiceStatus]} />
                                {e.invoiceStatus}
                            </Button>
                            <span>{e.paymentStatus}</span>
                        </>
                        :
                        <Row>
                            <Dot color={statusColors[e.invoiceStatus]} />
                            <span>
                                {(e.invoiceStatus !== 'Paid' && e.invoiceStatus !== 'Canceled') ? e.paymentStatus ? e.paymentStatus : e.invoiceStatus : e.invoiceStatus}
                            </span>
                        </Row>
                    }
                </td>
                <td>{formatCurrency(e.total)}</td>
                <td>{e.isAutoBilled ? 'Automatic' : 'Manual'}</td>
                <td>{shortHandDate(e.datePaid)}</td>
                <td><LinkTo
                    target='_blank'
                    to={'/reporting/sales/transaction/' + e.transRecordId}>{e.transRecordId}</LinkTo></td>
                <td>{e.description ?? '-'}</td>
            </ManagementRow>
        )
    }

    return <Table config={tableConfig} />
}

export default Invoices;